import { useEffect, useRef, useState, Ref, useContext } from 'react';
import { AppContext } from '../frontend/AppContext';
import { getLexicalHeaders, onLexicalHeadersUpdate } from './lexical/Editor/plugins/HashHighlightPlugin';

export function ArticleNavigation(props: { contentId: bigint | number, articleDom: HTMLBaseElement | null; }) {
    const [headers, setHeaders] = useState<Array<Header>>();
    const headersRef = useRef<Array<Header>>();
    const [redrawer, setRedrawer] = useState(0);
    const appContext = useContext(AppContext);

    const forceRedraw = () => {
        setRedrawer(i => i + 1);
    };

    const topThreshold = 150;

    const checkHeadersPositions = (scrollY: number) => {
        const hs = headersRef.current;
        if (!hs || hs.length == 0) return;

        let lastHiddenOne: Header | null = null;

        for (let i = 0; i < hs.length; i++) {
            const h = hs[i];

            h.isHighlighted = false;

            const bounds = h.element.getBoundingClientRect();

            if (bounds.y < topThreshold) {
                lastHiddenOne = h;
            }
        }

        if (lastHiddenOne) {
            lastHiddenOne.isHighlighted = true;
        } else {
            hs[0].isHighlighted = true;
        }

        forceRedraw();
    };

    useEffect(() => {
        const setupHeaders = () => {
            const h: Array<Header> = [];

            if (props.articleDom) {
                traverseDomForHeaders(props.articleDom, h);
            }

            // If no headers found in articleDom, try Lexical headers
            if (h.length === 0) {
                const lexicalHeaders = getLexicalHeaders();
                if (lexicalHeaders.length > 0) {
                    lexicalHeaders.forEach((element, index) => {
                        if (!isNullOrWhitespace(element.textContent)) {
                            h.push({
                                element: element as HTMLBaseElement,
                                isHighlighted: false,
                                index: index,
                                uid: index
                            });
                        }
                    });
                }
            }

            setHeaders(h);
            headersRef.current = h;

            checkHeadersPositions(0);

            forceRedraw();
        };

        setupHeaders();

        // For Lexical content, we need to retry as headers might load asynchronously
        const retryTimeout = setTimeout(() => {
            setupHeaders();
        }, 100);

        // Register for Lexical header updates
        const unsubscribe = onLexicalHeadersUpdate(setupHeaders);

        return () => {
            clearTimeout(retryTimeout);
            unsubscribe();
        };
    }, [props.articleDom, props.contentId]);

    let lastKnownScrollPosition = 0;
    let ticking = false;

    useEffect(() => {
        const handler = () => {
            lastKnownScrollPosition = window.scrollY;

            if (!ticking) {
                window.requestAnimationFrame(function () {
                    checkHeadersPositions(lastKnownScrollPosition);
                    ticking = false;
                });

                ticking = true;
            }
        };

        window.addEventListener('scroll', handler);

        return () => {
            window.removeEventListener('scroll', handler);
        };
    }, []);

    if (!headers || headers.length <= 1) {
        return null;
    }

    const getTextWithoutHash = (element: Node) => {
        let text = '';
        for (let i = 0; i < element.childNodes.length; ++i) {
            const childNode = element.childNodes[i];

            if ((childNode as HTMLElement).id == 'hash-btn') {
                continue;
            }

            if (childNode.nodeType === Node.TEXT_NODE) {
                text += childNode.textContent;
            } else {
                text += getTextWithoutHash(childNode);
            }
        }

        return text;
    };

    return <div className='text-sm text-gray-500 article-navigation pl-7'>
        <h5 className='mb-7'><i className="fa-solid fa-bars-staggered"></i> <strong>{appContext?.tenant.texts.articleNavigationHeaderText}</strong></h5>
        {
            headers?.map((header, i) => <div
                key={header.uid}
                className={`cursor-pointer hover:text-gray-700 mb-4 article-nav-link ${header.isHighlighted ? 'active' : ''}`}
                style={{ marginLeft: getIndentByLevel(getHeaderLevel(header.element.tagName)) }}
                onClick={() => {
                    header.element.scrollIntoView({
                        behavior: 'smooth'
                    });
                }}>
                {getTextWithoutHash(header.element)}
            </div>)
        }
    </div>;
}

function getIndentByLevel(level: number) {
    return ((level - 1) * 10) + 'px';
}

function getHeaderLevel(tagName: string) {
    return parseInt(tagName.substring(1));
}

const allowedHeaders = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'H7'];

function traverseDomForHeaders(el: HTMLBaseElement, headers: Array<Header>, masterIndex = 0) {
    for (let i = 0; i < el.children.length; i++) {
        const child = el.children[i] as HTMLBaseElement;

        if (allowedHeaders.indexOf(child.tagName) !== -1 && !isNullOrWhitespace(child.textContent)) {
            headers.push({
                element: child,
                isHighlighted: false,
                index: i,
                uid: masterIndex
            });
        }

        masterIndex++;

        traverseDomForHeaders(child, headers, masterIndex);
    }
}

type Header = {
    element: HTMLBaseElement;
    isHighlighted: boolean,
    index: number;
    uid: number;
};

function isNullOrWhitespace(input?: string | null) {
    return !input || !input.trim();
}